/**
 * DLUX cart drawer — inline variant picker.
 *
 * Renders a <select> on cart line items where the product has multiple variants
 * and a single option dimension (the common case for K-beauty size variants).
 * On change, swaps the variant via /cart/change.js and triggers the cart-drawer
 * to re-fetch its section, so the line item, totals, and free-ship bar update.
 *
 * Markup is in snippets/cart-drawer.liquid. Styles in assets/dlux-cart.css under
 * the `.dlux-cart-variant-picker` block.
 */
(function () {
  'use strict';

  // Track previous value so we can roll back if /cart/change.js rejects the swap.
  document.addEventListener(
    'focus',
    function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('dlux-cart-variant-picker')) {
        t.dataset.previousValue = t.value;
      }
    },
    true
  );

  // Listener runs in CAPTURE phase so we can stopPropagation BEFORE cart.js'
  // cart-items change handler sees the event. cart.js debounces all change
  // events as quantity validations, and would otherwise try to parse our
  // variant ID as a quantity and crash on resetQuantityInput(null).
  document.addEventListener(
    'change',
    function (e) {
      var select = e.target;
      if (!(select instanceof HTMLSelectElement)) return;
      if (!select.classList.contains('dlux-cart-variant-picker')) return;

      // Block cart-items' onChange / validateQuantity / resetQuantityInput chain.
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }

      var line = parseInt(select.dataset.line, 10);
      // Line item KEY ("variantId:hash") — stable identifier for the line.
      // CRITICAL: /cart/add prepends the new item to the TOP of the cart,
      // renumbering every line BEFORE our removal runs. Removing by `line`
      // number therefore deletes the WRONG line (when the swapped item was
      // line 1, it deleted the variant we just added — swap appeared to do
      // nothing; for lower lines it deleted a different product entirely).
      // The key is immune to renumbering.
      var lineKey = select.dataset.key || '';
      // Keep variant ID as a STRING. /cart/change expects id as a string —
      // the format regex `\A\d+\z` matches strings, not raw JS numbers, and
      // sending it as Number triggers HTTP 400 "expected Integer to be a regex".
      var newVariantId = String(select.value || '');
      var previousValue = select.dataset.previousValue || '';

      if ((!line && !lineKey) || !newVariantId) return;
    if (!window.routes || !window.routes.cart_change_url) {
      console.warn('[dlux-cart-variant-picker] routes.cart_change_url missing');
      return;
    }

    select.disabled = true;

    // 2-step variant swap: add the new variant first, then remove the old line.
    // This pattern works on every Shopify storefront (production + local dev
    // server) where /cart/change with `id` for variant swap can be rejected
    // with "no valid id or line parameter".
    //
    // Order matters: add first so if step 1 fails, the customer's cart is
    // unchanged. Shopify's /cart/add auto-merges if the new variant already
    // exists in the cart — no risk of duplicate lines.

    // Read the existing line's quantity from the line item's quantity input.
    var lineItem =
      select.closest('[id^="CartDrawer-Item-"]') ||
      select.closest('tr.cart-item') ||
      select.closest('.cart-item');
    var qtyInput = lineItem ? lineItem.querySelector('.quantity__input') : null;
    var existingQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // Loading state: reuse the line item's own spinner (rendered next to the
    // price in .cart-item__totals) and lock the quantity controls + remove
    // button so the line can't be mutated mid-swap.
    var lineSpinner = lineItem ? lineItem.querySelector('.cart-item__totals .loading__spinner') : null;
    var lockedControls = lineItem
      ? lineItem.querySelectorAll('.quantity__button, .quantity__input, cart-remove-button button, cart-remove-button a')
      : [];
    if (lineSpinner) lineSpinner.classList.remove('hidden');
    if (lineItem) lineItem.classList.add('dlux-variant-swapping');
    lockedControls.forEach(function (el) {
      el.setAttribute('disabled', 'disabled');
      el.setAttribute('aria-disabled', 'true');
    });

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: newVariantId, quantity: existingQty }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (addResult) {
        if (addResult && addResult.status) {
          // Shopify rejected the add (typically: variant unavailable / sold out).
          console.warn('[dlux-cart-variant-picker] add rejected:', addResult);
          if (previousValue) select.value = previousValue;
          throw new Error('add_failed');
        }
        // Step 2: remove the original line — by KEY, never by line number
        // (the add in step 1 renumbered all lines). Fall back to line number
        // only if the key attribute is somehow missing.
        //
        // Bundled section rendering: ask Shopify to return the re-rendered
        // cart-drawer section in this same response, so we don't need a third
        // round trip (GET /cart?section_id=cart-drawer) to update the UI.
        // The icon bubble shows total quantity, which a swap never changes,
        // so cart-drawer is the only section we need.
        var removeBody = lineKey ? { id: lineKey, quantity: 0 } : { line: line, quantity: 0 };
        removeBody.sections = 'cart-drawer';
        removeBody.sections_url = window.location.pathname;
        return fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(removeBody),
        });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (removeResult) {
        if (removeResult && removeResult.status) {
          // Old line removal failed. New variant has been added; cart now has
          // an extra line. Not ideal but better than losing the customer's
          // cart entirely. Log so we can monitor.
          console.warn(
            '[dlux-cart-variant-picker] remove failed, cart may have duplicate line:',
            removeResult
          );
        }

        // GTM dataLayer event for analytics.
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          try {
            window.dataLayer.push({
              event: 'cart_variant_change',
              line: line,
              variant_id: newVariantId,
              quantity: existingQty,
              source: 'cart_drawer',
            });
          } catch (_) {}
        }

        // Render the drawer from the bundled section in this response —
        // one less round trip than re-fetching. Mirrors CartDrawerItems'
        // getSectionsToRender (section: cart-drawer, selector: .drawer__inner).
        var rendered = false;
        try {
          var sectionHtml = removeResult && removeResult.sections && removeResult.sections['cart-drawer'];
          if (sectionHtml) {
            var newInner = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.drawer__inner');
            var curInner = document.querySelector('cart-drawer .drawer__inner');
            if (newInner && curInner) {
              curInner.innerHTML = newInner.innerHTML;
              rendered = true;
            }
          }
        } catch (renderErr) {
          console.warn('[dlux-cart-variant-picker] section render failed, falling back to refetch:', renderErr);
        }

        if (!rendered) {
          // Fallback: re-fetch the cart drawer section via the existing custom element.
          var cartDrawerItems = document.querySelector('cart-drawer-items');
          if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
            cartDrawerItems.onCartUpdate();
          }
        }

        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function (err) {
        if (err && err.message !== 'add_failed') {
          console.error('[dlux-cart-variant-picker] fetch failed:', err);
        }
        if (previousValue) select.value = previousValue;
      })
      .finally(function () {
        select.disabled = false;
        // On success the line item DOM was replaced by the section render, so
        // lineItem is disconnected and there's nothing to restore. Only undo
        // the loading state if the original node is still in the document
        // (i.e. the swap failed before re-render).
        if (lineItem && lineItem.isConnected) {
          lineItem.classList.remove('dlux-variant-swapping');
          if (lineSpinner) lineSpinner.classList.add('hidden');
          lockedControls.forEach(function (el) {
            el.removeAttribute('disabled');
            el.removeAttribute('aria-disabled');
          });
        }
      });
    },
    true // capture phase — intercept before cart.js' quantity-change handler
  );
})();
